import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, type PageMeta } from '@/lib/api';

/** RL-1817: an area of GET /audit-events/areas, in the order to show them. */
export interface AuditAreaOption {
  key: string;
  label: string;
}

export interface AuditEvent {
  id: string;
  occurredAt: string;
  action: string;
  /** RL-1816: plain words, e.g. "Refund approved". */
  label: string;
  area: string;
  areaLabel: string;
  /** Only read data (viewed or searched). */
  isView: boolean;
  actorType: 'staff' | 'system' | 'anonymous';
  actor: { id: string; fullName: string } | null;
  entityType: string | null;
  entityId: string | null;
  branch: { id: string; name: string } | null;
  requestId: string | null;
  ipAddress: string | null;
  metadata: Record<string, unknown>;
}

/** Filters of GET /audit-events (RL-1816); `from`/`to` are instants, `to` exclusive. */
export interface AuditQuery {
  area?: string;
  from?: string;
  to?: string;
  actorName?: string;
  entityType?: string;
  entityId?: string;
  changesOnly?: boolean;
  page: number;
}

export function useAuditAreas() {
  return useQuery({
    queryKey: ['audit-events', 'areas'],
    queryFn: ({ signal }) =>
      api
        .get<AuditAreaOption[]>('/audit-events/areas', { signal })
        .then((r) => r.data),
    staleTime: Infinity,
  });
}

export function useAuditEvents(query: AuditQuery) {
  return useQuery({
    queryKey: ['audit-events', query],
    queryFn: ({ signal }) =>
      api.get<AuditEvent[], PageMeta>('/audit-events', {
        signal,
        query: {
          ...query,
          changesOnly: query.changesOnly || undefined,
          pageSize: 50,
        },
      }),
    placeholderData: keepPreviousData,
  });
}
